class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Intercept fetch requests to update milestone on cart changes
    this.interceptCartRequests();
  }

  interceptCartRequests() {
    // Store reference to the cart drawer
    const cartDrawer = this;

    // Intercept fetch API
    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input.url;
      const isCartRequest = url &&
        (url.includes('/cart/add') ||
          url.includes('/cart/update') ||
          url.includes('/cart/change'));

      return originalFetch.apply(this, arguments)
        .then(function (response) {
          if (isCartRequest && cartDrawer.isOpen) {
            // Update milestone when drawer is already open
            setTimeout(() => {
              fetch('/cart.js')
                .then(res => res.json())
                .then(cart => {
                  cartDrawer.updateOfferMilestone(cart.total_price / 100);
                })
                .catch(error => console.error('Error fetching cart:', error));
            }, 500);
          }
          return response;
        });
    };
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))

      // Listen for cart updates to show loading
      document.addEventListener('cart:update', (event) => {
        if (this.isOpen) {
          this.showLoading();
        }
      });

      // Listen for cart updates completion to hide loading
      document.addEventListener('cart:updated', (event) => {
        if (this.isOpen) {
          // Check if cart is empty and hide loading immediately
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              if (cart.item_count === 0) {
                this.hideLoading();
              } else {
                setTimeout(() => this.hideLoading(), 300);
              }
              // Refresh cross-sell recommendations when cart updates
              setTimeout(() => this.refreshCrossSellRecommendations(), 500);
            })
            .catch(() => {
              // Hide loading on error
              this.hideLoading();
            });
        }
      });

      // Listen for cart changes to handle empty cart state
      document.addEventListener('cart:change', (event) => {
        if (this.isOpen && event.detail && event.detail.item_count === 0) {
          // Cart is empty, hide loading immediately
          this.hideLoading();
        }
        // Refresh cross-sell recommendations when cart changes
        if (this.isOpen) {
          setTimeout(() => this.refreshCrossSellRecommendations(), 300);
        }
      });
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
        this.updateOfferMilestone(event?.cart?.total_price / 100 || 0);
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    // Listen for changes to the cart (add/remove items)
    document.addEventListener('cart:change', (event) => {
      if (this.isOpen) {
        const cartTotalPrice = event?.detail?.cart?.total_price || 0;
        this.updateOfferMilestone(cartTotalPrice / 100);
      }
    });

    // Listen for cart update start events to show loading
    document.addEventListener('cart:update', (event) => {
      if (this.isOpen) {
        this.showLoading();
      }
    });

    // Listen for cart update completion events to hide loading
    document.addEventListener('cart:updated', (event) => {
      if (this.isOpen) {
        this.hideLoading();
      }
    });

    // Listen for item removal clicks
    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('wt-cart__item__remove') ||
        event.target.closest('.wt-cart__item__remove')) {
        // Show loading when any item is being removed
        this.showLoading();

        // Set a timeout to hide loading if cart operations don't complete
        setTimeout(() => {
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              if (cart.item_count === 0) {
                // Cart is empty, hide loading immediately
                this.hideLoading();
              }
            })
            .catch(error => {
              console.error('Error fetching cart:', error);
              // Hide loading on error
              this.hideLoading();
            });
        }, 1000);
      }
    });

    // Listen for quantity changes in the cart
    document.addEventListener('click', (event) => {
      if (this.isOpen && (event.target.classList.contains('js-counter-button') ||
        event.target.closest('.js-counter-button'))) {
        // Show loading when quantity is being changed
        this.showLoading();

        setTimeout(() => {
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              this.updateOfferMilestone(cart.total_price / 100);
            })
            .catch(error => console.error('Error fetching cart:', error))
            .finally(() => {
              // Hide loading after quantity update
              setTimeout(() => this.hideLoading(), 500);
            });
        }, 300); // Small delay to allow cart to update
      }
    });

    // Listen for quantity input changes
    document.addEventListener('change', (event) => {
      if (this.isOpen && event.target.classList.contains('js-counter-quantity')) {
        // Show loading when quantity input changes
        this.showLoading();

        setTimeout(() => {
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              this.updateOfferMilestone(cart.total_price / 100);
            })
            .catch(error => console.error('Error fetching cart:', error))
            .finally(() => {
              // Hide loading after quantity update
              setTimeout(() => this.hideLoading(), 500);
            });
        }, 300); // Small delay to allow cart to update
      }
    });

    document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    // Show loading indicator
    this.showLoading();

    const sectionsToRender = this.getSectionsToRender();
    fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((response) => {
        const parsedState = {
          "sections": response
        }
        this.renderContents(parsedState, false);

        // Get current cart total
        return fetch('/cart.js');
      })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        return res.json();
      })
      .then(cart => {
        this.updateOfferMilestone(cart.total_price / 100);

        // Refresh cross-sell recommendations with new rotation
        this.refreshCrossSellRecommendations();
      })
      .catch(error => {
        console.error('Error refreshing cart drawer', error);
      })
      .finally(() => {
        // Hide loading indicator
        this.hideLoading();
      });
  }

  updateOfferMilestone(cartValue) {
    const offerMilestone = this.querySelector('.wt-offer-milestone');
    if (!offerMilestone) return;

    const tier1 = parseInt(offerMilestone.dataset.tier1);
    const tier2 = parseInt(offerMilestone.dataset.tier2);
    const tier3 = parseInt(offerMilestone.dataset.tier3);

    let targetThreshold = tier1;
    let offerMessage = "";
    let remainingAmount = tier1 - cartValue;

    // Check if we're using price-based or quantity-based milestone
    const offerMilestoneType = offerMilestone.dataset.milestoneType || window.theme?.settings?.offer_milestone_type || 'price';

    // Get appropriate message templates based on milestone type
    let messageBelow1, messageAt1, messageAt2, messageAt3;

    if (offerMilestoneType === 'price') {
      messageBelow1 = window.theme?.settings?.offer_message_below_tier1 || "Shop for ₹{amount} to get 15% OFF";
      messageAt1 = window.theme?.settings?.offer_message_tier1 || "Yay! You got 15% OFF! Shop for ₹{amount} more to get 20% OFF + a Free Gift";
      messageAt2 = window.theme?.settings?.offer_message_tier2 || "YAY! You got 20% OFF + Free Gift! Shop for ₹{amount} more to get Flat 25% OFF";
      messageAt3 = window.theme?.settings?.offer_message_tier3 || "YAY! You got Flat 25% OFF!";
    } else {
      messageBelow1 = window.theme?.settings?.qty_offer_message_below_tier1 || "Add {amount} more items to get Free Shipping";
      messageAt1 = window.theme?.settings?.qty_offer_message_tier1 || "Yay! You got Free Shipping! Add {amount} more items to get 20% OFF";
      messageAt2 = window.theme?.settings?.qty_offer_message_tier2 || "Yay! You got 20% OFF! Add {amount} more item to get 25% OFF";
      messageAt3 = window.theme?.settings?.qty_offer_message_tier3 || "Yay! You got 25% OFF + Free Shipping";
    }

    // For quantity-based milestone, we need to get the cart item count instead of price
    if (offerMilestoneType === 'quantity') {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          // Exclude upsell product (ID: 9934720368957) from quantity count
          const UPSELL_PRODUCT_ID = 9934720368957;
          const filteredItemCount = cart.items.filter(item => item.product_id !== UPSELL_PRODUCT_ID)
            .reduce((total, item) => total + item.quantity, 0);
          this.updateOfferMilestoneUI(filteredItemCount, tier1, tier2, tier3, messageBelow1, messageAt1, messageAt2, messageAt3);
        })
        .catch(error => console.error('Error fetching cart:', error));
      return;
    }

    // For price-based milestone, we need to exclude upsell product from cart value
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // Exclude upsell product (ID: 9934720368957) from price calculation
        const UPSELL_PRODUCT_ID = 9934720368957;
        const filteredCartValue = cart.items.filter(item => item.product_id !== UPSELL_PRODUCT_ID)
          .reduce((total, item) => total + (item.line_price), 0) / 100;

        // Use filtered cart value for price-based milestone calculation
        this.updateOfferMilestoneUI(filteredCartValue, tier1, tier2, tier3, messageBelow1, messageAt1, messageAt2, messageAt3);
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
        // Fallback to original cartValue if fetch fails
        this.updateOfferMilestoneUI(cartValue, tier1, tier2, tier3, messageBelow1, messageAt1, messageAt2, messageAt3);
      });
    return;
  }

  updateOfferMilestoneUI(value, tier1, tier2, tier3, messageBelow1, messageAt1, messageAt2, messageAt3) {
    const offerMilestone = this.querySelector('.wt-offer-milestone');
    if (!offerMilestone) return;

    let offerMessage = "";
    let remainingAmount = 0;
    let progressPercentage = 0;

    // Determine which tier the value is in and set appropriate message
    if (value < tier1) {
      remainingAmount = tier1 - value;
      offerMessage = messageBelow1.replace('{amount}', remainingAmount.toFixed(0));
      progressPercentage = 5;
    } else if (value < tier2) {
      remainingAmount = tier2 - value;
      offerMessage = messageAt1.replace('{amount}', remainingAmount.toFixed(0));
      progressPercentage = 33;
    } else if (value < tier3) {
      remainingAmount = tier3 - value;
      offerMessage = messageAt2.replace('{amount}', remainingAmount.toFixed(0));
      progressPercentage = 70;
    } else {
      offerMessage = messageAt3;
      progressPercentage = 100;
    }

    // Update text
    const textElement = offerMilestone.querySelector('.wt-offer-milestone__text');
    if (textElement) {
      textElement.textContent = offerMessage;
    }

    // Update progress bar
    const progressBar = offerMilestone.querySelector('.wt-progress-bar__fill');
    if (progressBar) {
      progressBar.style.width = `${progressPercentage}%`;
    }

    // Update tier indicators
    const tierElements = offerMilestone.querySelectorAll('.wt-offer-milestone__tier');
    if (tierElements.length) {
      // Use tier thresholds for activation instead of just the current tier
      const tierThresholds = [tier1, tier2, tier3];
      tierElements.forEach((tier, index) => {
        if (value >= tierThresholds[index]) {
          tier.classList.add('active');
        } else {
          tier.classList.remove('active');
        }
      });
    }

    // Check and remove upsell product if it's alone in the cart
    this.checkAndRemoveUpsellIfAlone();
  }

  // Function to check and remove upsell product if it's alone in the cart
  checkAndRemoveUpsellIfAlone() {
    return fetch('/cart.js')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(cart => {
        // Check if ALL items in cart are upsell products (no main products left)
        if (cart.items.length > 0) {
          const UPSELL_VARIANT_ID = 50321883660605;
          const allAreUpsell = cart.items.every(item => item.variant_id === UPSELL_VARIANT_ID);

          if (allAreUpsell) {
            // Remove all upsell products by clearing the cart
            return fetch('/cart/clear.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            })
              .then(response => {
                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
              })
              .then(updatedCart => {
                // Refresh cart drawer
                document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
                return updatedCart;
              });
          }
        }
        return cart;
      })
      .catch(error => {
        console.error('Error removing upsell product', error);
        return null;
      });
  }

  refreshCrossSellRecommendations() {
    const crossSellSection = this.querySelector('.wt-cart__cross-sell');
    if (!crossSellSection) return;

    // Add a timestamp parameter to force refresh with new rotation
    const timestamp = Date.now();
    const url = `${routes.cart_url}?section_id=cart-drawer&t=${timestamp}`;

    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newCrossSell = html.querySelector('.wt-cart__cross-sell');

        if (newCrossSell) {
          crossSellSection.innerHTML = newCrossSell.innerHTML;

          // Reinitialize any sliders in the cross-sell section
          const sliders = crossSellSection.querySelectorAll('[data-swiper]');
          sliders.forEach(slider => {
            if (window.Swiper && typeof window.initializeSwiper === 'function') {
              window.initializeSwiper(slider);
            }
          });
        }
      })
      .catch(error => {
        console.error('Error refreshing cross-sell recommendations:', error);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  showLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
      return;
    }

    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.className = 'cart-drawer-loading-overlay';
    this.loadingOverlay.innerHTML = `
      <div class="loading-overlay__spinner">
        <div class="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    `;

    this.drawer.appendChild(this.loadingOverlay);

    // Add styles if not already added
    if (!document.getElementById('cart-drawer-loading-styles')) {
      const style = document.createElement('style');
      style.id = 'cart-drawer-loading-styles';
      style.textContent = `
        .cart-drawer-loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .cart-drawer-loading-overlay .loading-overlay__spinner {
          width: 3rem;
          height: 3rem;
          display: inline-block;
        }
        
        .cart-drawer-loading-overlay .lds-ring {
           --loader-size: 3rem;
           color: #c43f3e;
         }
         
         .cart-drawer-loading-overlay.hidden {
           display: none !important;
         }
      `;
      document.head.appendChild(style);
    }
  }

  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.add('hidden');
    }
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', (event) => {
      if (event.target.classList.contains('js-counter-quantity')) {
        const cartDrawer = this.closest('cart-drawer');
        if (cartDrawer) {
          cartDrawer.showLoading();
        }

        setTimeout(() => {
          if (cartDrawer) {
            fetch('/cart.js')
              .then(res => res.json())
              .then(cart => {
                cartDrawer.updateOfferMilestone(cart.total_price / 100);
              })
              .catch(error => console.error('Error fetching cart:', error))
              .finally(() => {
                setTimeout(() => cartDrawer.hideLoading(), 500);
              });
          }
        }, 300);
      }
    });

    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('js-counter-button') ||
        event.target.closest('.js-counter-button')) {
        const cartDrawer = this.closest('cart-drawer');
        if (cartDrawer) {
          cartDrawer.showLoading();
        }

        setTimeout(() => {
          if (cartDrawer) {
            fetch('/cart.js')
              .then(res => res.json())
              .then(cart => {
                cartDrawer.updateOfferMilestone(cart.total_price / 100);
              })
              .catch(error => console.error('Error fetching cart:', error))
              .finally(() => {
                setTimeout(() => cartDrawer.hideLoading(), 500);
              });
          }
        }, 300);
      }
    });
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
